/**
 * Metafield purposes — SINGLE SOURCE for the org_shopify_metafield_mappings
 * purpose union (BLU-536 / Phase 56.1).
 *
 * Pure type/data module — no imports, no DB types. Everything that needs the
 * purpose set derives from this file:
 *   - DB CHECK constraints `org_metafield_mappings_purpose_check` +
 *     `org_metafield_mappings_purpose_scope_consistent` (migration
 *     20260612000000) — defense-in-depth mirror, kept in lockstep manually.
 *   - Backend `metafield-mappings.types.ts` re-exports the union and derives
 *     `deriveScopeFromPurpose` from the scope sets below.
 *   - Backend router `shopify-metafield-mappings.ts` derives its z.enum from
 *     `METAFIELD_PURPOSES` (no parallel literal list).
 *   - Frontend (Phase 56.1 Plan 07) imports this same module for its
 *     purpose rows + variant-scope grouping.
 *
 * History: design_id + back_design_id (BLU-432), design_variant (BLU-498),
 * the remaining 8 (BLU-536) — front/back Design Shape, front/back variant,
 * front/back platen (store-only), plus fulfillment_type + pretreat which are
 * mappable purposes ONLY in this phase (consumers: BLU-508 / a later
 * pretreat ticket). `print_sides` is deliberately ABSENT — sides are derived
 * from design-ID presence (BLU-498 D4), never an input metafield.
 *
 * `custom_print_sku` (BLU-528 / Phase 59.1) is a DIFFERENT CLASS of purpose: a
 * **routing override**, not a composition input. It maps a Shopify variant
 * metafield to OUR conforming `CUST-…` print SKU for clients whose native
 * Shopify SKU doesn't match the print grammar; its consumer is order-sync
 * intake (→ `org_variant_custom_print_skus` → `prod_garment_units.shopify_sku`),
 * NOT the `print_file_lookups` filename composition the other purposes feed.
 *
 * `front_artwork` / `back_artwork` (Feature B1) are a THIRD class: direct
 * per-variant print-file **keys** (bare storage filenames, not design IDs).
 * Consumed by the `design_id`-independent artwork sync leg
 * (`variant-artwork-sync.ts`) which writes them straight into
 * `print_file_lookups` — no SKU-grammar parse, no composition. Per org, a
 * SKU's print file comes from exactly ONE of the three legs (design_id
 * composition / custom_print_sku routing / variant artwork) — the artwork
 * leg no-ops for any org with a `design_id` mapping and skips
 * custom_print_sku-remapped variants.
 *
 * `front_design_platen_variant` / `back_design_platen_variant` (Phase 86,
 * D-16) join the two platen purposes as write-only print INFO: Print Run
 * auto-fills all four from the org's Platen size rules on every artwork write
 * (D-17); nobody types them. Print time never reads them back. The platen and
 * platen variant a SKU prints with come from `org_platen_variant_files`.
 *
 * `front_mockup_preview` / `back_mockup_preview` (BLU-1018) are a FOURTH
 * class and the only one that never touches a print file: they point at a
 * VARIANT metafield holding the garment MOCKUP image (a Shopify
 * `file_reference` whose value is a File GID, or a text field holding an
 * `https://` link). Their consumer is the Packing screen preview only, via
 * a dedicated preview table. They must never reach `print_file_lookups`,
 * `org_variant_artwork_urls`, or filename composition — a mockup routed to
 * the printer would be printed onto the garment.
 */
/**
 * The full purpose tuple. Order matters only for display/back-compat: the
 * three pre-BLU-536 purposes come first. Append new purposes at the END and
 * change the DB CHECK in the same PR. (Phase 86's two platen-variant purposes
 * sit next to their platen pair instead; nothing reads the tuple by index and
 * the DB CHECK is a set, so position is display-only.)
 *
 * DEPLOY ORDER — the one rule, applied in either direction: at no instant may
 * running code write a value the LIVE constraint rejects. Which side moves
 * first therefore depends on whether the CHECK widens or narrows.
 *
 *   WIDENING (adding a purpose, as BLU-1018 did): the MIGRATION goes first,
 *   the code second. The widened CHECK accepts everything the old one did, so
 *   the pre-deploy backend keeps working against it. Deploy the code first and
 *   every write of the new purpose is rejected with 23514 until the migration
 *   lands — the operator saves a mapping and gets an error.
 *
 *   NARROWING (removing a purpose): the CODE goes first, the migration second.
 *   Ship the backend that no longer writes the value, clear the rows that still
 *   carry it, and only then narrow the CHECK — a narrowing applied while the
 *   old backend is live rejects its writes, and applied while old rows exist it
 *   fails outright.
 */
export const METAFIELD_PURPOSES = [
    'design_id',
    'back_design_id',
    'design_variant',
    'front_design_shape',
    'back_design_shape',
    'front_design_variant',
    'back_design_variant',
    'front_design_platen',
    'back_design_platen',
    'front_design_platen_variant',
    'back_design_platen_variant',
    'fulfillment_type',
    'pretreat',
    'custom_print_sku',
    'front_artwork',
    'back_artwork',
    'print_technology',
    'front_mockup_preview',
    'back_mockup_preview',
];
/**
 * Purposes whose metafield lives on the Shopify Product. The two design IDs
 * (Phase 56.1 decision 3) plus `print_technology` (BLU-676 — the print
 * method is a whole-product decision, one value per product, mirrored onto
 * every POD variant). Everything else varies by size/variant.
 *
 * `front_mockup_preview` / `back_mockup_preview` are deliberately ABSENT:
 * the mockup differs per colourway, so the field lives on the VARIANT. That
 * was confirmed against the live store before the purposes were added, so
 * there is no parent-product fallback anywhere in this feature.
 */
export const PRODUCT_SCOPED_PURPOSES = Object.freeze(new Set(['design_id', 'back_design_id', 'print_technology']));
/**
 * Purposes whose metafield lives on the Shopify ProductVariant — the
 * complement of PRODUCT_SCOPED_PURPOSES, derived (not listed) so the two
 * sets can never drift from the tuple.
 */
export const VARIANT_SCOPED_PURPOSES = Object.freeze(new Set(METAFIELD_PURPOSES.filter((purpose) => !PRODUCT_SCOPED_PURPOSES.has(purpose))));
